import { ApolloQueryResult } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import {
  EnumOwnerType,
  EnumUserAccessOperatorsStatus,
  Exact,
  FilterFindOneUserInput,
  InputMaybe,
  OperatorsByUserQuery,
  OrganizationsByUserQuery,
  useOperatorsByUserLazyQuery,
  useOrganizationsByUserLazyQuery,
  UserByIdQuery,
  UserFindOneDocument,
  UserFindOneQuery,
  useUserFindOneQuery,
  useUserUpdateMutation,
} from "gql/generated";
import _ from "lodash";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useDocumentTitle, useLoginRedirectPath } from "shared/hooks";

export type AppContextValues = {
  dbUser: UserByIdQuery["userById"];
  dbUserError: boolean;
  dbUserLoading: boolean;
  operators: OperatorsByUserQuery["operatorsByUser"];
  operatorsError: boolean;
  operatorsLoading: boolean;
  organizations: OrganizationsByUserQuery["organizationsByUser"];
  organizationsError: boolean;
  organizationsLoading: boolean;
  isNavigationBlocked: boolean;
  isForcingLogout: boolean;
  refetchDbUser: (
    variables?:
      | Partial<
          Exact<{
            filter?: InputMaybe<FilterFindOneUserInput>;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<UserFindOneQuery>>;
  setIsNavigationBlocked: Dispatch<SetStateAction<boolean>>;
  updateUserAccessStatus: (
    type: EnumOwnerType,
    entityId: string,
  ) => Promise<void>;
};

const context = createContext<AppContextValues | null>(null);

export const AppContextProvider = ({ children }: PropsWithChildren) => {
  useDocumentTitle();

  /**
   * Load the user, their operators, and their organizations
   */
  const { user, logout } = useAuth0();
  const auth0Id = user?.sub ?? "";

  /**
   * If a user is logged in and then returns to the base route (/) and enters
   * a different email address, we need to ensure the currently logged in user
   * is logged out and that the new user is prompted to login
   *
   * The code below runs in a useEffect because if it doesn't the onboarding
   * screen redraws/reanimates during the logout phase
   */
  const { clearLoginRedirectPath } = useLoginRedirectPath();
  const [isForcingLogout, setIsForcingLogout] = useState(false);
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  useEffect(() => {
    if (user?.email && email && user.email !== email) {
      setIsForcingLogout(true);
      clearLoginRedirectPath();
      logout({
        logoutParams: {
          returnTo: `${window.location.origin}/app?email=${email.toLowerCase()}`,
        },
      });
    }
  }, [email, user, clearLoginRedirectPath, logout, setIsForcingLogout]);

  const {
    data: dbUser,
    error: dbUserError,
    loading: dbUserLoading,
    refetch: refetchDbUser,
  } = useUserFindOneQuery({
    variables: { filter: { auth0Id } },
  });

  const [
    getUserOrganizations,
    { data: organizations, error: orgError, loading: orgLoading },
  ] = useOrganizationsByUserLazyQuery();

  const [
    getUserOperators,
    { data: operators, error: opError, loading: opLoading },
  ] = useOperatorsByUserLazyQuery();

  // Refetch the user's operators and organizations when the dbUser is updated
  useEffect(() => {
    getUserOperators();
    getUserOrganizations();
  }, [dbUser, user, getUserOperators, getUserOrganizations]);

  if (dbUserError || opError || orgError) {
    throw new Error("Unable to load the user");
  }

  const [isNavigationBlocked, setIsNavigationBlocked] = useState(false);

  /**
   * When a user clicks on an item from the Doorstop page, we need to (maybe) update their access status
   */
  const [userUpdateMutation] = useUserUpdateMutation({
    refetchQueries: [UserFindOneDocument],
  });
  const updateUserAccessStatus = useCallback(
    async (type: EnumOwnerType, entityId: string) => {
      const user = dbUser?.userFindOne;
      if (!user) {
        return;
      }
      const accessType = `${type.toLowerCase()}s` as
        | "operators"
        | "organizations";
      const currentStatus = user.access[accessType]?.find(
        (item) => item?.entityId === entityId,
      );
      if (currentStatus?.status !== EnumUserAccessOperatorsStatus.Accepted) {
        const access = _.compact(
          user.access[accessType]?.map((item) =>
            item?.entityId !== entityId
              ? item
              : {
                  entityId,
                  role: item?.role,
                  status: EnumUserAccessOperatorsStatus.Accepted,
                },
          ),
        );
        try {
          const response = await userUpdateMutation({
            variables: {
              id: user._id,
              record: {
                access: {
                  ...user.access, // We must spread the current user access, otherwise you will wipe out the other accessType's data
                  [accessType]: access,
                },
              },
            },
          });
          if (response.errors) {
            throw new Error("Unable to update user access status");
          }
        } catch {
          throw new Error("Unable to update user access status");
        }
      }
    },
    [dbUser, userUpdateMutation],
  );

  /**
   * Add an onbeforeunload event listener to the window object when there
   * are unsaved changes to block accidental closing of the tab/data loss
   */
  useEffect(() => {
    window.onbeforeunload = isNavigationBlocked
      ? (e: Event) => {
          e.preventDefault();
          return "block";
        }
      : null;

    return () => {
      window.onbeforeunload = null;
    };
  }, [isNavigationBlocked]);

  /**
   * Set the context value
   */
  const contextValue: AppContextValues = useMemo(
    () => ({
      dbUser: dbUser?.userFindOne,
      dbUserError: dbUserError ?? false,
      dbUserLoading: dbUserLoading ?? false,
      operators: operators?.operatorsByUser ?? [],
      organizations: organizations?.organizationsByUser ?? [],
      operatorsError: opError ?? false,
      operatorsLoading: opLoading ?? false,
      organizationsError: orgError ?? false,
      organizationsLoading: orgLoading ?? false,
      isNavigationBlocked,
      isForcingLogout,
      refetchDbUser,
      setIsNavigationBlocked,
      updateUserAccessStatus,
    }),
    [
      dbUser,
      dbUserError,
      dbUserLoading,
      operators,
      opError,
      opLoading,
      organizations,
      orgError,
      orgLoading,
      isNavigationBlocked,
      isForcingLogout,
      refetchDbUser,
      setIsNavigationBlocked,
      updateUserAccessStatus,
    ],
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

export const useAppContext = () => {
  const appContext = useContext(context);

  if (!appContext) {
    throw new Error("useAppContext must be used within an AppContextProvider");
  }

  return appContext;
};
